// frontend/src/pages/ContasAPagarPage.js
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import useApi from '../hooks/useApi';
import { 
    PlusCircle, X, Loader2, Calendar as CalendarIcon, DollarSign, List, Edit, Trash2, Info, 
    AlertTriangle, CheckCircle, Package, ArrowRight, Banknote, KeySquare, Receipt,
    ChevronDown, ChevronUp, Search, Filter, ArrowLeft, RefreshCw, Clock
} from 'lucide-react';

import NovoRecebimentoModal from '../components/modals/NovoRecebimentoModal';

// --- Helper para formatar data em UTC ---
const formatDateUTC = (dateString) => {
    if (!dateString) return '-';
    const dateOnly = dateString.split('T')[0]; 
    return new Date(dateOnly).toLocaleDateString('pt-BR', { timeZone: 'UTC' });
};

// --- HOOK: useDebounce ---
const useDebounce = (value, delay) => {
    const [debouncedValue, setDebouncedValue] = useState(value);
    useEffect(() => {
        const handler = setTimeout(() => { setDebouncedValue(value); }, delay);
        return () => { clearTimeout(handler); };
    }, [value, delay]);
    return debouncedValue;
};

// --- COMPONENTE DE BUSCA DE FORNECEDOR ---
const FornecedorSearchInput = ({ onFornecedorSelect, initialFornecedor = null, allowFreeText = false }) => {
    const { apiFetch } = useApi();
    const [searchTerm, setSearchTerm] = useState(initialFornecedor ? initialFornecedor.name : '');
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selected, setSelected] = useState(initialFornecedor);
    const debouncedSearchTerm = useDebounce(searchTerm, 300);
    const wrapperRef = useRef(null);

    useEffect(() => {
        if (debouncedSearchTerm && !selected) {
            setLoading(true);
            apiFetch(`/contas-a-pagar/fornecedores?search=${debouncedSearchTerm}`)
                .then(data => setResults(data))
                .catch(err => console.error(err))
                .finally(() => setLoading(false));
        } else {
            setResults([]);
        }
    }, [debouncedSearchTerm, apiFetch, selected]);

    useEffect(() => {
        function handleClickOutside(event) {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setResults([]);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [wrapperRef]);

    const handleSelect = (fornecedor) => {
        setSearchTerm(fornecedor.name);
        setSelected(fornecedor);
        onFornecedorSelect(fornecedor);
        setResults([]);
    };

    const handleInputChange = (e) => {
        setSearchTerm(e.target.value);
        setSelected(null); 
        if (allowFreeText) {
            onFornecedorSelect({ id: null, name: e.target.value });
        } else {
            onFornecedorSelect(null); 
        }
    };

    return (
        <div className="relative" ref={wrapperRef}>
            <input
                type="text"
                value={searchTerm}
                onChange={handleInputChange}
                placeholder="Pesquisar fornecedor (Nome, CPF/CNPJ...)"
                className="w-full p-2 mt-1 border rounded-md focus:ring-blue-500"
                required
            />
            {loading && <Loader2 size={16} className="animate-spin absolute right-3 top-3.5 text-gray-400" />}
            {results.length > 0 && (
                <ul className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                    {results.map(f => (
                        <li 
                            key={f.id} 
                            onClick={() => handleSelect(f)}
                            className="p-3 hover:bg-gray-100 cursor-pointer"
                        >
                            <p className="font-medium">{f.name}</p>
                            <p className="text-sm text-gray-500">{f.document_id || `Cód: ${f.client_number}`}</p>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

// ============================================================================
// NOVO MODAL: PROGRAMAÇÃO DE PAGAMENTOS (A AGENDA)
// ============================================================================
const ProgramacaoPagamentoModal = ({ onClose, formatCurrency }) => {
    const { apiFetch } = useApi();
    const [schedules, setSchedules] = useState([]);
    const [categories, setCategories] = useState([]); // NOVO ESTADO
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    
    // Paginação e Busca
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [search, setSearch] = useState('');
    const debouncedSearch = useDebounce(search, 300);

    // Formulário (Criar/Editar)
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [editingSchedule, setEditingSchedule] = useState(null);
    const [formData, setFormData] = useState({
        supplier_id: null,
        supplier_name: '',
        description: '',
        amount: '',
        due_date: '',
        recurrence: 'none',
        category_name: '' // NOVO CAMPO
    });

    // Busca Categorias Cadastradas
    useEffect(() => {
        apiFetch('/categories')
            .then(data => {
                if (Array.isArray(data)) {
                    // Filtra apenas categorias ativas de despesa
                    setCategories(data.filter(c => c.status === 'ativo' && (c.type === 'expense' || c.type === 'bill_payment' || c.type === 'transfer')));
                }
            })
            .catch(console.error);
    }, [apiFetch]);

    const fetchSchedules = useCallback(async () => {
        setLoading(true);
        try {
            const data = await apiFetch(`/contas-a-pagar/schedule?page=${page}&limit=12&search=${debouncedSearch}`);
            setSchedules(data.schedules || []);
            setTotalPages(data.totalPages || 1);
        } catch (err) {
            setError('Erro ao carregar a programação.');
        } finally {
            setLoading(false);
        }
    }, [apiFetch, page, debouncedSearch]);

    useEffect(() => {
        fetchSchedules();
    }, [fetchSchedules]);

    useEffect(() => {
        if (page !== 1) setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedSearch]);

    const handleOpenForm = (schedule = null) => {
        if (schedule) {
            setEditingSchedule(schedule);
            setFormData({
                supplier_id: schedule.supplier_id,
                supplier_name: schedule.supplier_name,
                description: schedule.description,
                amount: schedule.amount,
                due_date: schedule.due_date,
                recurrence: schedule.recurrence,
                category_name: schedule.category_name || ''
            });
        } else {
            setEditingSchedule(null);
            setFormData({ supplier_id: null, supplier_name: '', description: '', amount: '', due_date: '', recurrence: 'none', category_name: '' });
        }
        setIsFormOpen(true);
    };

    const handleSaveForm = async (e) => {
        e.preventDefault();
        // A validação agora checa o NOME do fornecedor/beneficiário, não o ID
        if (!formData.supplier_name || !formData.description || !formData.amount || !formData.due_date) {
            alert('Preencha todos os campos obrigatórios.');
            return;
        }

        try {
            if (editingSchedule) {
                await apiFetch(`/contas-a-pagar/schedule/${editingSchedule.id}`, { method: 'PUT', body: formData });
            } else {
                await apiFetch('/contas-a-pagar/schedule', { method: 'POST', body: formData });
            }
            setIsFormOpen(false);
            fetchSchedules();
        } catch (err) {
            alert(`Erro ao salvar: ${err.message}`);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Tem certeza que deseja excluir esta programação?')) return;
        try {
            await apiFetch(`/contas-a-pagar/schedule/${id}`, { method: 'DELETE' });
            fetchSchedules();
        } catch (err) {
            alert(`Erro ao excluir: ${err.message}`);
        }
    };

    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-50 p-2 md:p-4 animate-in fade-in">
            <div className="bg-gray-50 rounded-xl shadow-2xl w-full max-w-5xl max-h-[95vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
                <div className="flex items-center justify-between p-4 md:p-6 bg-white border-b">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-indigo-100 text-indigo-700 rounded-lg"><CalendarIcon size={24} /></div>
                        <div>
                            <h2 className="text-xl font-bold text-gray-800">Programação de Pagamentos (Agenda)</h2>
                            <p className="text-sm text-gray-500">Programe contas recorrentes ou futuras para o Dashboard.</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-2 rounded-full hover:bg-gray-200 transition-colors"><X size={24} /></button>
                </div>

                <div className="flex-1 overflow-y-auto p-4 md:p-6">
                    {isFormOpen ? (
                        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                            <h3 className="text-lg font-bold mb-4">{editingSchedule ? 'Editar Programação' : 'Nova Programação'}</h3>
                            <form onSubmit={handleSaveForm} className="space-y-4">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Fornecedor / Beneficiário*</label>
                                        <FornecedorSearchInput 
                                            allowFreeText={true}
                                            initialFornecedor={editingSchedule ? { id: formData.supplier_id, name: formData.supplier_name } : null}
                                            onFornecedorSelect={(f) => setFormData({...formData, supplier_id: f?.id || null, supplier_name: f?.name || ''})}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Descrição / Motivo*</label>
                                        <input type="text" required value={formData.description} onChange={e => setFormData({...formData, description: e.target.value})} className="w-full p-2 border rounded-md focus:ring-2 focus:ring-indigo-500" placeholder="Ex: Aluguel da Loja" />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Categoria (Opcional)</label>
                                        <select value={formData.category_name} onChange={e => setFormData({...formData, category_name: e.target.value})} className="w-full p-2 border rounded-md focus:ring-2 focus:ring-indigo-500 bg-white">
                                            <option value="">Nenhuma Categoria (Livre)</option>
                                            {categories.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Valor Programado*</label>
                                        <input type="number" step="0.01" required value={formData.amount} onChange={e => setFormData({...formData, amount: e.target.value})} className="w-full p-2 border rounded-md focus:ring-2 focus:ring-indigo-500" />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Data (Primeiro Vencimento)*</label>
                                        <input type="date" required value={formData.due_date} onChange={e => setFormData({...formData, due_date: e.target.value})} className="w-full p-2 border rounded-md focus:ring-2 focus:ring-indigo-500" />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Recorrência</label>
                                        <select value={formData.recurrence} onChange={e => setFormData({...formData, recurrence: e.target.value})} className="w-full p-2 border rounded-md focus:ring-2 focus:ring-indigo-500 bg-white">
                                            <option value="none">Única (Não repete)</option>
                                            <option value="monthly">Mensal (Todo mês neste dia)</option>
                                            <option value="yearly">Anual (Todo ano neste dia)</option>
                                        </select>
                                    </div>
                                </div>
                                <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
                                    <button type="button" onClick={() => setIsFormOpen(false)} className="px-4 py-2 bg-gray-200 text-gray-800 font-bold rounded-lg hover:bg-gray-300">Cancelar</button>
                                    <button type="submit" className="px-4 py-2 bg-indigo-600 text-white font-bold rounded-lg hover:bg-indigo-700">Salvar Programação</button>
                                </div>
                            </form>
                        </div>
                    ) : (
                        <>
                            <div className="flex flex-col md:flex-row justify-between gap-4 mb-4">
                                <div className="relative w-full md:w-96">
                                    <Search className="absolute left-3 top-2.5 text-gray-400" size={18}/>
                                    <input type="text" placeholder="Buscar por fornecedor ou descrição..." value={search} onChange={e => setSearch(e.target.value)} className="w-full pl-10 pr-4 py-2 border rounded-lg shadow-sm focus:ring-2 focus:ring-indigo-500" />
                                </div>
                                <button onClick={() => handleOpenForm()} className="flex items-center justify-center gap-2 bg-indigo-600 text-white px-4 py-2 rounded-lg font-bold shadow-sm hover:bg-indigo-700 transition-colors">
                                    <PlusCircle size={18}/> Nova Programação
                                </button>
                            </div>

                            {loading ? (
                                <div className="flex justify-center p-10"><Loader2 className="animate-spin text-indigo-600" size={32} /></div>
                            ) : schedules.length === 0 ? (
                                <div className="bg-white p-10 rounded-xl shadow-sm text-center border border-gray-200">
                                    <CalendarIcon className="mx-auto text-gray-300 mb-3" size={48} />
                                    <h3 className="text-lg font-bold text-gray-700">Nenhuma programação encontrada</h3>
                                    <p className="text-gray-500">Clique em "Nova Programação" para agendar pagamentos recorrentes ou futuros.</p>
                                </div>
                            ) : (
                                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                                    <div className="overflow-x-auto">
                                        <table className="w-full text-sm text-left">
                                            <thead className="bg-gray-100 text-gray-600 uppercase text-xs tracking-wider">
                                                <tr>
                                                    <th className="px-4 py-3">Beneficiário/Fornecedor</th>
                                                    <th className="px-4 py-3">Descrição e Categoria</th>
                                                    <th className="px-4 py-3 text-center">Data / Recorrência</th>
                                                    <th className="px-4 py-3 text-right">Valor</th>
                                                    <th className="px-4 py-3 text-center">Status Agenda</th>
                                                    <th className="px-4 py-3 text-center">Ações</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-100">
                                                {schedules.map(sch => (
                                                    <tr key={sch.id} className="hover:bg-gray-50">
                                                        <td className="px-4 py-3 font-bold text-gray-800">{sch.supplier_name}</td>
                                                        <td className="px-4 py-3 text-gray-600">
                                                            <div>{sch.description}</div>
                                                            {sch.category_name && <div className="text-xs text-indigo-500 font-medium">{sch.category_name}</div>}
                                                        </td>
                                                        <td className="px-4 py-3 text-center">
                                                            <div className="font-medium text-gray-900">{formatDateUTC(sch.due_date)}</div>
                                                            <div className="text-[10px] text-indigo-600 font-bold uppercase">{sch.recurrence === 'monthly' ? 'Mensal' : sch.recurrence === 'yearly' ? 'Anual' : 'Única'}</div>
                                                        </td>
                                                        <td className="px-4 py-3 text-right font-bold text-gray-900">{formatCurrency(sch.amount)}</td>
                                                        <td className="px-4 py-3 text-center">
                                                            {sch.status === 'completed' 
                                                                ? <span className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs font-bold">Feito</span>
                                                                : <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded text-xs font-bold">Pendente</span>
                                                            }
                                                        </td>
                                                        <td className="px-4 py-3 text-center">
                                                            <div className="flex items-center justify-center gap-2">
                                                                <button onClick={() => handleOpenForm(sch)} className="p-1.5 text-gray-500 hover:text-indigo-600 hover:bg-indigo-50 rounded" title="Editar"><Edit size={16}/></button>
                                                                <button onClick={() => handleDelete(sch.id)} className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded" title="Excluir"><Trash2 size={16}/></button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                    {totalPages > 1 && (
                                        <div className="p-3 border-t bg-gray-50 flex justify-between items-center">
                                            <span className="text-sm text-gray-500">Pág {page} de {totalPages}</span>
                                            <div className="flex gap-2">
                                                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="px-3 py-1 bg-white border rounded text-sm disabled:opacity-50">Anterior</button>
                                                <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="px-3 py-1 bg-white border rounded text-sm disabled:opacity-50">Próxima</button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

// --- COMPONENTE: MODAL DE NOVA FATURA ---
const NovaFaturaModal = ({ onClose, onSave, formatCurrency }) => {
    const { apiFetch } = useApi();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    
    const [selectedFornecedor, setSelectedFornecedor] = useState(null);
    const [numeroNF, setNumeroNF] = useState('');
    const [dataEmissao, setDataEmissao] = useState(new Date().toISOString().split('T')[0]);
    const [valorTotalFatura, setValorTotalFatura] = useState(0);
    const [observacoes, setObservacoes] = useState('');

    const [numeroParcelas, setNumeroParcelas] = useState(1);
    const [meioPagamento, setMeioPagamento] = useState('pix'); 
    const [parcelas, setParcelas] = useState([]);

    useEffect(() => {
        const total = parcelas.reduce((acc, p) => acc + (parseFloat(p.valor_parcela) || 0), 0);
        setValorTotalFatura(total);
    }, [parcelas]);

    useEffect(() => {
        const dataBase = dataEmissao || new Date().toISOString().split('T')[0];
        
        setParcelas(Array.from({ length: numeroParcelas }, (_, i) => {
            const vencimento = new Date(dataBase);
            vencimento.setMonth(vencimento.getMonth() + (i + 1));
            
            return {
                id: i,
                numero_parcela: i + 1,
                data_vencimento: vencimento.toISOString().split('T')[0],
                valor_parcela: 0,
                pix_chave: meioPagamento === 'pix' ? '' : null,
                boleto_numero: meioPagamento === 'boleto' ? '' : null,
                boleto_cod_barras: meioPagamento === 'boleto' ? '' : null,
            };
        }));
    }, [numeroParcelas, meioPagamento, dataEmissao]);

    const handleParcelaChange = (index, field, value) => {
        const novasParcelas = [...parcelas];
        novasParcelas[index] = { ...novasParcelas[index], [field]: value };
        setParcelas(novasParcelas);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        if (!selectedFornecedor || !selectedFornecedor.id) {
            setError('Por favor, pesquise e selecione um fornecedor cadastrado para a Fatura.');
            setLoading(false);
            return;
        }

        const faturaData = {
            fatura: {
                fornecedor_id: parseInt(selectedFornecedor.id, 10),
                nf_numero: numeroNF || null,
                data_emissao: dataEmissao,
                valor_total: valorTotalFatura,
                observacoes: observacoes || null,
            },
            parcelas: parcelas.map(p => ({
                numero_parcela: p.numero_parcela,
                data_vencimento: p.data_vencimento,
                valor_parcela: parseFloat(p.valor_parcela) || 0,
                meio_pagamento: meioPagamento,
                pix_chave: p.pix_chave || null,
                boleto_numero: p.boleto_numero || null,
                boleto_cod_barras: p.boleto_cod_barras || null,
            }))
        };

        try {
            await apiFetch('/contas-a-pagar', {
                method: 'POST',
                body: faturaData,
            });
            onSave();
        } catch (err) {
            console.error("Erro ao salvar fatura:", err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
                <div className="flex items-center justify-between p-4 border-b">
                    <h2 className="text-xl font-bold text-gray-800">Lançar Nova Fatura a Pagar</h2>
                    <button onClick={onClose} className="p-2 rounded-full hover:bg-gray-200"><X size={20} /></button>
                </div>
                <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
                    <div className="p-6 space-y-6">
                        {error && <p className="p-3 text-sm text-red-700 bg-red-100 rounded-md">{error}</p>}
                        <div className="p-4 border rounded-lg">
                            <h3 className="mb-4 text-lg font-semibold">Dados da Fatura</h3>
                            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Fornecedor*</label>
                                    <FornecedorSearchInput 
                                        allowFreeText={false}
                                        onFornecedorSelect={setSelectedFornecedor}
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Nº da NF</label>
                                    <input type="text" value={numeroNF} onChange={e => setNumeroNF(e.target.value)} className="w-full p-2 mt-1 border rounded-md" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Data de Emissão*</label>
                                    <input type="date" value={dataEmissao} onChange={e => setDataEmissao(e.target.value)} className="w-full p-2 mt-1 border rounded-md" required />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Nº de Parcelas*</label>
                                    <select value={numeroParcelas} onChange={e => setNumeroParcelas(Number(e.target.value))} className="w-full p-2 mt-1 border rounded-md">
                                        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(n => <option key={n} value={n}>{n}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Meio de Pagamento*</label>
                                    <select value={meioPagamento} onChange={e => setMeioPagamento(e.target.value)} className="w-full p-2 mt-1 border rounded-md">
                                        <option value="pix">PIX</option>
                                        <option value="boleto">Boleto</option>
                                        <option value="dinheiro">Dinheiro</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Valor Total da Fatura</label>
                                    <p className="w-full p-2 mt-1 font-bold text-gray-800">
                                        {formatCurrency(valorTotalFatura, 'BRL')}
                                    </p>
                                </div>
                                <div className="md:col-span-3">
                                    <label className="block text-sm font-medium text-gray-700">Observações</label>
                                    <textarea 
                                        value={observacoes} 
                                        onChange={e => setObservacoes(e.target.value)} 
                                        className="w-full p-2 mt-1 border rounded-md"
                                        rows="2"
                                        placeholder="Detalhes adicionais sobre a fatura..."
                                    ></textarea>
                                </div>
                            </div>
                        </div>
                        <div className="p-4 border rounded-lg">
                            <h3 className="mb-4 text-lg font-semibold">Parcelas</h3>
                            <div className="space-y-4">
                                {parcelas.map((parcela, index) => (
                                    <div key={parcela.id} className="p-3 bg-gray-50 rounded-md">
                                        <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
                                            <div>
                                                <label className="text-xs font-medium text-gray-600">Parcela</label>
                                                <input type="text" value={parcela.numero_parcela} disabled className="w-full p-2 mt-1 bg-gray-200 border rounded-md" />
                                            </div>
                                            <div>
                                                <label className="text-xs font-medium text-gray-600">Vencimento*</label>
                                                <input type="date" value={parcela.data_vencimento} onChange={e => handleParcelaChange(index, 'data_vencimento', e.target.value)} className="w-full p-2 mt-1 border rounded-md" required />
                                            </div>
                                            <div>
                                                <label className="text-xs font-medium text-gray-600">Valor*</label>
                                                <input type="number" step="0.01" value={parcela.valor_parcela} onChange={e => handleParcelaChange(index, 'valor_parcela', e.target.value)} className="w-full p-2 mt-1 border rounded-md" required />
                                            </div>
                                            {meioPagamento === 'pix' && (
                                                <div>
                                                    <label className="text-xs font-medium text-gray-600">Chave PIX</label>
                                                    <input type="text" value={parcela.pix_chave || ''} onChange={e => handleParcelaChange(index, 'pix_chave', e.target.value)} className="w-full p-2 mt-1 border rounded-md" />
                                                </div>
                                            )}
                                            {meioPagamento === 'boleto' && (
                                                <>
                                                    <div>
                                                        <label className="text-xs font-medium text-gray-600">Nº do Boleto</label>
                                                        <input type="text" value={parcela.boleto_numero || ''} onChange={e => handleParcelaChange(index, 'boleto_numero', e.target.value)} className="w-full p-2 mt-1 border rounded-md" />
                                                    </div>
                                                    <div className="md:col-span-2">
                                                        <label className="text-xs font-medium text-gray-600">Cód. de Barras</label>
                                                        <input type="text" value={parcela.boleto_cod_barras || ''} onChange={e => handleParcelaChange(index, 'boleto_cod_barras', e.target.value)} className="w-full p-2 mt-1 border rounded-md" />
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                    <div className="flex justify-end gap-2 p-4 mt-4 bg-gray-50 border-t">
                        <button type="button" onClick={onClose} className="px-4 py-2 font-semibold bg-gray-200 rounded-md">Cancelar</button>
                        <button type="submit" disabled={loading} className="flex items-center justify-center px-4 py-2 font-semibold text-white bg-blue-600 rounded-md disabled:bg-blue-300">
                            {loading ? <Loader2 className="animate-spin" /> : 'Salvar Fatura'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

// --- COMPONENTE: MODAL DE EDIÇÃO DE FATURA ---
const EditFaturaModal = ({ onClose, onSave, faturaId, formatCurrency }) => {
    const { apiFetch } = useApi();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    
    const [faturaHeader, setFaturaHeader] = useState(null);
    const [parcelas, setParcelas] = useState([]);
    const [originalParcelas, setOriginalParcelas] = useState([]); 
    const [meioPagamento, setMeioPagamento] = useState('pix');
    const [selectedFornecedor, setSelectedFornecedor] = useState(null);

    useEffect(() => {
        const fetchFaturaData = async () => {
            setLoading(true);
            try {
                const data = await apiFetch(`/contas-a-pagar/fatura/${faturaId}`);
                setFaturaHeader({
                    fornecedor_id: data.fornecedor_id,
                    nf_numero: data.nf_numero || '',
                    data_emissao: data.data_emissao.split('T')[0],
                    observacoes: data.observacoes || '',
                    valor_total: data.valor_total
                });
                setSelectedFornecedor({ id: data.fornecedor_id, name: data.fornecedor_nome });
                setParcelas(data.parcelas || []);
                setOriginalParcelas(JSON.parse(JSON.stringify(data.parcelas || []))); 
                setMeioPagamento(data.parcelas[0]?.meio_pagamento || 'pix');
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };
        fetchFaturaData();
    }, [apiFetch, faturaId]);

    useEffect(() => {
        const total = parcelas.reduce((acc, p) => acc + (parseFloat(p.valor_parcela) || 0), 0);
        setFaturaHeader(h => (h ? { ...h, valor_total: total } : null));
    }, [parcelas]);

    const handleHeaderChange = (field, value) => {
        setFaturaHeader(h => ({ ...h, [field]: value }));
    };

    const handleParcelaChange = (index, field, value) => {
        const novasParcelas = [...parcelas];
        novasParcelas[index] = { ...novasParcelas[index], [field]: value };
        setParcelas(novasParcelas);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);
        
        const headerData = {
            ...faturaHeader,
            fornecedor_id: selectedFornecedor.id,
        };

        const promiseCabecalho = apiFetch(`/contas-a-pagar/fatura/${faturaId}`, {
            method: 'PUT',
            body: headerData,
        });

        const promisesParcelas = parcelas
            .filter((p, i) => 
                p.status !== 'paga' && 
                JSON.stringify(p) !== JSON.stringify(originalParcelas[i]) 
            )
            .map(p => 
                apiFetch(`/contas-a-pagar/parcela/${p.id}`, {
                    method: 'PUT',
                    body: {
                        data_vencimento: p.data_vencimento,
                        valor_parcela: p.valor_parcela,
                        pix_chave: p.pix_chave || null,
                        boleto_numero: p.boleto_numero || null,
                        boleto_cod_barras: p.boleto_cod_barras || null,
                    }
                })
            );

        try {
            await Promise.all([promiseCabecalho, ...promisesParcelas]);
            onSave(); 
        } catch (err) {
            console.error("Erro ao salvar alterações da fatura:", err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    if (loading && !faturaHeader) {
        return (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
                <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl p-6">
                    <Loader2 className="animate-spin m-auto" />
                </div>
            </div>
        );
    }

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
                <div className="flex items-center justify-between p-4 border-b">
                    <h2 className="text-xl font-bold text-gray-800">Editar Fatura (NF: {faturaHeader?.nf_numero || 'S/N'})</h2>
                    <button onClick={onClose} className="p-2 rounded-full hover:bg-gray-200"><X size={20} /></button>
                </div>
                <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
                    <div className="p-6 space-y-6">
                        {error && <p className="p-3 text-sm text-red-700 bg-red-100 rounded-md">{error}</p>}
                        <div className="p-4 border rounded-lg">
                            <h3 className="mb-4 text-lg font-semibold">Dados da Fatura</h3>
                            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Fornecedor*</label>
                                    <FornecedorSearchInput 
                                        allowFreeText={false}
                                        initialFornecedor={selectedFornecedor}
                                        onFornecedorSelect={setSelectedFornecedor}
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Nº da NF</label>
                                    <input type="text" value={faturaHeader?.nf_numero || ''} onChange={e => handleHeaderChange('nf_numero', e.target.value)} className="w-full p-2 mt-1 border rounded-md" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Data de Emissão*</label>
                                    <input type="date" value={faturaHeader?.data_emissao || ''} onChange={e => handleHeaderChange('data_emissao', e.target.value)} className="w-full p-2 mt-1 border rounded-md" required />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Nº de Parcelas</label>
                                    <input value={parcelas.length} className="w-full p-2 mt-1 bg-gray-200 border rounded-md" disabled />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Meio de Pagamento</label>
                                    <input value={meioPagamento} className="w-full p-2 mt-1 bg-gray-200 border rounded-md" disabled />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Valor Total da Fatura</label>
                                    <p className="w-full p-2 mt-1 font-bold text-gray-800">
                                        {formatCurrency(faturaHeader?.valor_total || 0, 'BRL')}
                                    </p>
                                </div>
                                <div className="md:col-span-3">
                                    <label className="block text-sm font-medium text-gray-700">Observações</label>
                                    <textarea 
                                        value={faturaHeader?.observacoes || ''} 
                                        onChange={e => handleHeaderChange('observacoes', e.target.value)} 
                                        className="w-full p-2 mt-1 border rounded-md"
                                        rows="2"
                                        placeholder="Detalhes adicionais sobre a fatura..."
                                    ></textarea>
                                </div>
                            </div>
                        </div>
                        <div className="p-4 border rounded-lg">
                            <h3 className="mb-4 text-lg font-semibold">Parcelas</h3>
                            <div className="space-y-4">
                                {parcelas.map((parcela, index) => {
                                    const isPaga = parcela.status === 'paga';
                                    return (
                                        <div key={parcela.id} className={`p-3 rounded-md ${isPaga ? 'bg-gray-200' : 'bg-gray-50'}`}>
                                            <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
                                                <div>
                                                    <label className="text-xs font-medium text-gray-600">Parcela</label>
                                                    <input type="text" value={parcela.numero_parcela} disabled className="w-full p-2 mt-1 bg-gray-200 border rounded-md" />
                                                </div>
                                                <div>
                                                    <label className="text-xs font-medium text-gray-600">Vencimento*</label>
                                                    <input type="date" value={parcela.data_vencimento} onChange={e => handleParcelaChange(index, 'data_vencimento', e.target.value)} className={`w-full p-2 mt-1 border rounded-md ${isPaga ? 'bg-gray-300' : ''}`} required disabled={isPaga} />
                                                </div>
                                                <div>
                                                    <label className="text-xs font-medium text-gray-600">Valor*</label>
                                                    <input type="number" step="0.01" value={parcela.valor_parcela} onChange={e => handleParcelaChange(index, 'valor_parcela', e.target.value)} className={`w-full p-2 mt-1 border rounded-md ${isPaga ? 'bg-gray-300' : ''}`} required disabled={isPaga} />
                                                </div>
                                                {meioPagamento === 'pix' && (
                                                    <div>
                                                        <label className="text-xs font-medium text-gray-600">Chave PIX</label>
                                                        <input type="text" value={parcela.pix_chave || ''} onChange={e => handleParcelaChange(index, 'pix_chave', e.target.value)} className={`w-full p-2 mt-1 border rounded-md ${isPaga ? 'bg-gray-300' : ''}`} disabled={isPaga} />
                                                    </div>
                                                )}
                                                {meioPagamento === 'boleto' && (
                                                    <>
                                                        <div>
                                                            <label className="text-xs font-medium text-gray-600">Nº do Boleto</label>
                                                            <input type="text" value={parcela.boleto_numero || ''} onChange={e => handleParcelaChange(index, 'boleto_numero', e.target.value)} className={`w-full p-2 mt-1 border rounded-md ${isPaga ? 'bg-gray-300' : ''}`} disabled={isPaga} />
                                                        </div>
                                                        <div className="md:col-span-2">
                                                            <label className="text-xs font-medium text-gray-600">Cód. de Barras</label>
                                                            <input type="text" value={parcela.boleto_cod_barras || ''} onChange={e => handleParcelaChange(index, 'boleto_cod_barras', e.target.value)} className={`w-full p-2 mt-1 border rounded-md ${isPaga ? 'bg-gray-300' : ''}`} disabled={isPaga} />
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                    <div className="flex justify-end gap-2 p-4 mt-4 bg-gray-50 border-t">
                        <button type="button" onClick={onClose} className="px-4 py-2 font-semibold bg-gray-200 rounded-md">Cancelar</button>
                        <button type="submit" disabled={loading} className="flex items-center justify-center px-4 py-2 font-semibold text-white bg-blue-600 rounded-md disabled:bg-blue-300">
                            {loading ? <Loader2 className="animate-spin" /> : 'Salvar Alterações'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

// --- COMPONENTE: MODAL DE PAGAMENTO ---
const PagamentoModal = ({ onClose, onSave, fatura, accounts, formatCurrency, selectedCurrency, accountTypes }) => {
    const { apiFetch } = useApi();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const [selectedParcelaIds, setSelectedParcelaIds] = useState([]);
    const [valorPagoTotal, setValorPagoTotal] = useState(0);
    const [descontoTotal, setDescontoTotal] = useState(0);
    const [jurosTotal, setJurosTotal] = useState(0);
    const [dataPagamento, setDataPagamento] = useState(new Date().toISOString().split('T')[0]);
    const [accountId, setAccountId] = useState('');

    const contasPagamento = useMemo(() => {
        const tiposCartao = (accountTypes || [])
            .filter(at => at.name.toLowerCase().includes('cartão'))
            .map(at => at.id);
            
        return accounts.filter(a => 
            a.status === 'ativo' && 
            a.currency === selectedCurrency &&
            !tiposCartao.includes(a.account_type_id)
        );
    }, [accounts, selectedCurrency, accountTypes]);

    const parcelasPendentes = useMemo(() => {
        return fatura.parcelas.filter(p => p.status === 'a vencer' || p.status === 'vencida');
    }, [fatura.parcelas]);

    useEffect(() => {
        const total = parcelasPendentes
            .filter(p => selectedParcelaIds.includes(p.id))
            .reduce((acc, p) => acc + p.valor_parcela, 0);
        
        const desconto = parseFloat(descontoTotal) || 0;
        const juros = parseFloat(jurosTotal) || 0; 
        
        setValorPagoTotal(total - desconto + juros); 
    }, [selectedParcelaIds, parcelasPendentes, descontoTotal, jurosTotal]); 

    const handleSelectParcela = (parcelaId) => {
        setSelectedParcelaIds(prev =>
            prev.includes(parcelaId) ? prev.filter(id => id !== parcelaId) : [...prev, parcelaId]
        );
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        if (selectedParcelaIds.length === 0) {
            setError('Selecione pelo menos uma parcela para pagar.');
            setLoading(false);
            return;
        }
        if (!accountId) {
            setError('Selecione a conta de débito.');
            setLoading(false);
            return;
        }

        const pagamentoData = {
            parcela_ids: selectedParcelaIds,
            account_id: parseInt(accountId, 10),
            data_pagamento: dataPagamento,
            valor_pago_total: parseFloat(valorPagoTotal) || 0,
            desconto_total: parseFloat(descontoTotal) || 0,
            juros_total: parseFloat(jurosTotal) || 0,
        };

        try {
            await apiFetch('/contas-a-pagar/pagar', {
                method: 'POST',
                body: pagamentoData,
            });
            onSave();
        } catch (err) {
            console.error("Erro ao processar pagamento:", err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
                <div className="flex items-center justify-between p-4 border-b">
                    <h2 className="text-xl font-bold text-gray-800">Efetuar Pagamento</h2>
                    <button onClick={onClose} className="p-2 rounded-full hover:bg-gray-200"><X size={20} /></button>
                </div>
                <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
                    <div className="p-6 space-y-6">
                        {error && <p className="p-3 text-sm text-red-700 bg-red-100 rounded-md">{error}</p>}
                        <div>
                            <h3 className="font-semibold">Fatura: {fatura.nf_numero || 'N/A'}</h3>
                            <p className="text-sm text-gray-600">Fornecedor: {fatura.fornecedor_nome}</p>
                        </div>
                        <div className="p-4 border rounded-lg">
                            <h3 className="mb-4 text-lg font-semibold">Selecionar Parcelas</h3>
                            <div className="space-y-2 max-h-48 overflow-y-auto">
                                {parcelasPendentes.map(p => (
                                    <label key={p.id} className={`flex items-center p-3 rounded-md border ${selectedParcelaIds.includes(p.id) ? 'bg-blue-50 border-blue-300' : 'bg-white'}`}>
                                        <input 
                                            type="checkbox" 
                                            checked={selectedParcelaIds.includes(p.id)}
                                            onChange={() => handleSelectParcela(p.id)}
                                            className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                                        />
                                        <span className="ml-3 font-medium">Parcela {p.numero_parcela}</span>
                                        <span className="ml-auto font-semibold">{formatCurrency(p.valor_parcela, selectedCurrency)}</span>
                                        <span className="ml-2 text-xs text-gray-500">({new Date(p.data_vencimento + 'T00:00:00').toLocaleDateString('pt-BR')})</span>
                                    </label>
                                ))}
                                {parcelasPendentes.length === 0 && <p className="text-sm text-gray-500">Não há parcelas pendentes para esta fatura.</p>}
                            </div>
                        </div>
                        <div className="p-4 border rounded-lg">
                            <h3 className="mb-4 text-lg font-semibold">Detalhes do Pagamento</h3>
                            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Pagar com a Conta*</label>
                                    <select value={accountId} onChange={e => setAccountId(e.target.value)} className="w-full p-2 mt-1 border rounded-md" required>
                                            <option value="">Selecione...</option>
                                            {contasPagamento.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Data do Pagamento*</label>
                                    <input type="date" value={dataPagamento} onChange={e => setDataPagamento(e.target.value)} className="w-full p-2 mt-1 border rounded-md" required />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Desconto Total (R$)</label>
                                    <input 
                                        type="number" 
                                        step="0.01" 
                                        value={descontoTotal} 
                                        onChange={e => setDescontoTotal(parseFloat(e.target.value) || 0)} 
                                        className="w-full p-2 mt-1 border rounded-md" 
                                    />
                                </div>
                                
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Juros (R$)</label>
                                    <input 
                                        type="number" 
                                        step="0.01" 
                                        value={jurosTotal} 
                                        onChange={e => setJurosTotal(parseFloat(e.target.value) || 0)} 
                                        className="w-full p-2 mt-1 border rounded-md" 
                                    />
                                </div>
                                
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-medium text-gray-700">Valor Final Pago</label>
                                    <p className="w-full p-2 mt-1 text-xl font-bold text-gray-800">
                                        {formatCurrency(valorPagoTotal, selectedCurrency)}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="flex justify-end gap-2 p-4 mt-4 bg-gray-50 border-t">
                        <button type="button" onClick={onClose} className="px-4 py-2 font-semibold bg-gray-200 rounded-md">Cancelar</button>
                        <button 
                            type="submit" 
                            disabled={loading || selectedParcelaIds.length === 0 || !accountId} 
                            className="flex items-center justify-center px-4 py-2 font-semibold text-white bg-green-600 rounded-md disabled:bg-green-300"
                        >
                            {loading ? <Loader2 className="animate-spin" /> : 'Confirmar Pagamento'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

// --- COMPONENTE: MODAL DE CONFIRMAÇÃO DE EXCLUSÃO ---
const ConfirmDeleteModal = ({ onClose, onConfirm, loading, error }) => {
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md" onClick={e => e.stopPropagation()}>
                <div className="flex items-start justify-between p-4 border-b">
                    <div className="flex items-center gap-3">
                        <AlertTriangle className="text-red-500" size={24} />
                        <h2 className="text-xl font-bold text-gray-800">Excluir Fatura</h2>
                    </div>
                    <button onClick={onClose} className="p-2 rounded-full hover:bg-gray-200"><X size={20} /></button>
                </div>
                <div className="p-6">
                    {error ? (
                        <p className="p-3 text-sm text-red-700 bg-red-100 rounded-md">{error}</p>
                    ) : (
                        <>
                            <p className="text-gray-600">Tem certeza de que deseja excluir esta fatura e todas as suas parcelas? Esta ação não pode ser desfeita.</p>
                            <p className="mt-2 p-3 text-sm text-yellow-800 bg-yellow-100 rounded-md">
                                <span className="font-bold">Atenção:</span> Só é possível excluir faturas que não tenham nenhuma parcela paga.
                            </p>
                        </>
                    )}
                </div>
                <div className="flex justify-end gap-2 p-4 bg-gray-50 border-t">
                    <button type="button" onClick={onClose} disabled={loading} className="px-4 py-2 font-semibold bg-gray-200 rounded-md">Cancelar</button>
                    {!error && (
                        <button 
                            onClick={onConfirm}
                            disabled={loading} 
                            className="flex items-center justify-center px-4 py-2 font-semibold text-white bg-red-600 rounded-md disabled:bg-red-300"
                        >
                            {loading ? <Loader2 className="animate-spin" /> : 'Sim, Excluir'}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

// --- COMPONENTE PRINCIPAL DA PÁGINA ---
const ContasAPagarPage = ({ accounts, onDataChange, formatCurrency, selectedCurrency, dataChangeKey, accountTypes }) => {
    const { apiFetch, userProfile, permissions } = useApi(); 
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    
    const [faturas, setFaturas] = useState([]);
    
    // Estados de Paginação e Filtro
    const [totalPages, setTotalPages] = useState(1);
    const [currentPage, setCurrentPage] = useState(1);
    const [searchQuery, setSearchQuery] = useState('');
    const [filterMeioPagamento, setFilterMeioPagamento] = useState('');
    const [filterStatus, setFilterStatus] = useState(''); 

    // Estado para alternar tipo de filtro de data
    const [filterDateType, setFilterDateType] = useState('month'); 
    const [filterDateValue, setFilterDateValue] = useState(() => {
        const now = new Date();
        return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    });

    const [expandedFaturaId, setExpandedFaturaId] = useState(null); 
    const [deletingFaturaId, setDeletingFaturaId] = useState(null);
    const [editingFaturaId, setEditingFaturaId] = useState(null);
    
    const [isNovaFaturaOpen, setIsNovaFaturaOpen] = useState(false);
    const [isProgramacaoModalOpen, setIsProgramacaoModalOpen] = useState(false); // NOVO MODAL
    
    const [isPagamentoOpen, setIsPagamentoOpen] = useState(false);
    const [selectedFatura, setSelectedFatura] = useState(null);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [deleteError, setDeleteError] = useState('');

    const debouncedSearchQuery = useDebounce(searchQuery, 300);

    const canCreate = permissions?.payables_create || userProfile?.is_super_user;
    const canEdit = permissions?.payables_edit || userProfile?.is_super_user;
    const canDelete = permissions?.payables_delete || userProfile?.is_super_user;


    const fetchData = useCallback(async (page = 1, search = '', filterMP = '', filterStatus = '', dateType, dateVal) => {
        setLoading(true);
        setError('');
        try {
            const params = new URLSearchParams({
                page: page,
                limit: 12,
                search: search,
                meio_pagamento: filterMP,
            });
            
            if (filterStatus && filterStatus !== 'Todos') {
                params.append('status', filterStatus);
            }

            if (dateVal) {
                if (dateType === 'date') params.append('date', dateVal);
                else params.append('month', dateVal);
            }
            
            const url = `/contas-a-pagar?${params.toString()}`;
            const data = await apiFetch(url);
            
            const processedFaturas = data.faturas.map(f => ({
                ...f,
                parcelas: (typeof f.parcelas === 'string' ? JSON.parse(f.parcelas) : f.parcelas) || []
            }));
            
            setFaturas(processedFaturas);
            setTotalPages(data.totalPages || 1);
            
        } catch (err) {
            console.error("Erro ao buscar dados de contas a pagar:", err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [apiFetch]); 

    useEffect(() => {
        fetchData(currentPage, debouncedSearchQuery, filterMeioPagamento, filterStatus, filterDateType, filterDateValue);
    }, [currentPage, debouncedSearchQuery, filterMeioPagamento, filterStatus, filterDateType, filterDateValue, dataChangeKey, fetchData]);

    useEffect(() => {
        if (currentPage !== 1) setCurrentPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedSearchQuery, filterMeioPagamento, filterStatus, filterDateType, filterDateValue]);

    const handleOpenPagamento = (fatura) => {
        setSelectedFatura(fatura);
        setIsPagamentoOpen(true);
    };

    const handleOpenEditModal = (faturaId) => { setEditingFaturaId(faturaId); };
    const handleDeleteClick = (faturaId) => { setDeleteError(''); setDeletingFaturaId(faturaId); };

    const handleCloseModals = () => {
        setIsNovaFaturaOpen(false);
        setIsPagamentoOpen(false);
        setSelectedFatura(null);
        setEditingFaturaId(null);
        setDeletingFaturaId(null);
    };

    const handleSave = () => {
        handleCloseModals();
        onDataChange(); 
    };

    const handleConfirmDelete = async () => {
        if (!deletingFaturaId) return;
        setDeleteLoading(true);
        setDeleteError('');
        try {
            await apiFetch(`/contas-a-pagar/fatura/${deletingFaturaId}`, { method: 'DELETE' });
            handleSave(); 
        } catch (err) {
            console.error("Erro ao excluir fatura:", err);
            setDeleteError(err.message);
        } finally {
            setDeleteLoading(false);
        }
    };
    
    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= totalPages) setCurrentPage(newPage);
    };

    const toggleFaturaExpand = (faturaId) => {
        if (expandedFaturaId === faturaId) setExpandedFaturaId(null);
        else setExpandedFaturaId(faturaId);
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case 'paga': return <span className="px-2 py-0.5 text-xs font-medium text-green-800 bg-green-100 rounded-full"><CheckCircle size={12} className="inline mr-1" />Paga</span>;
            case 'vencida': return <span className="px-2 py-0.5 text-xs font-medium text-red-800 bg-red-100 rounded-full"><AlertTriangle size={12} className="inline mr-1" />Vencida</span>;
            case 'a vencer': default: return <span className="px-2 py-0.5 text-xs font-medium text-blue-800 bg-blue-100 rounded-full"><Info size={12} className="inline mr-1" />A Vencer</span>;
        }
    };
    
    const getMeioPagamentoIcon = (meio) => {
        switch (meio) {
            case 'pix': return <KeySquare size={16} className="text-green-500" title="PIX" />;
            case 'boleto': return <Receipt size={16} className="text-blue-500" title="Boleto" />;
            case 'dinheiro': return <Banknote size={16} className="text-gray-500" title="Dinheiro" />;
            default: return <DollarSign size={16} className="text-gray-400" />;
        }
    };

    return (
        <div className="p-4 md:p-6 relative">
            
            {/* NOVO MODAL DE PROGRAMAÇÃO DE PAGAMENTO */}
            {isProgramacaoModalOpen && (
                <ProgramacaoPagamentoModal 
                    onClose={() => setIsProgramacaoModalOpen(false)}
                    formatCurrency={formatCurrency}
                />
            )}

            {/* CABEÇALHO */}
            <div className="flex flex-col md:flex-row justify-between md:items-center mb-6 gap-4">
                <div>
                    <h1 className="text-3xl font-bold text-gray-800">Contas a Pagar</h1>
                    <p className="text-gray-600">Gira as suas faturas e pagamentos a fornecedores.</p>
                </div>
                {canCreate && (
                    <div className="flex gap-2">
                        {/* NOVO BOTÃO AGENDA DE PAGAMENTOS */}
                        <button
                            onClick={() => setIsProgramacaoModalOpen(true)}
                            className="flex items-center justify-center gap-2 px-4 py-2 font-semibold text-indigo-700 bg-indigo-100 rounded-md hover:bg-indigo-200 transition-colors shadow-sm"
                        >
                            <CalendarIcon size={18} />
                            <span className="hidden sm:inline">Programação de Pagamento</span>
                            <span className="sm:hidden">Agenda</span>
                        </button>
                        
                        <button
                            onClick={() => setIsNovaFaturaOpen(true)}
                            className="flex items-center justify-center gap-2 px-4 py-2 font-semibold text-white bg-blue-600 rounded-md hover:bg-blue-700 shadow-sm"
                        >
                            <PlusCircle size={18} />
                            <span className="hidden sm:inline">Lançar Nova Fatura</span>
                            <span className="sm:hidden">Nova Fatura</span>
                        </button>
                    </div>
                )}
            </div>

            {/* Barra de Filtro e Pesquisa (Layout Grid) */}
            <div className="mb-4 p-4 bg-white rounded-lg shadow-sm border border-gray-100">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-end">
                    <div className="md:col-span-3">
                        <label className="block text-sm font-medium text-gray-700">Pesquisar</label>
                        <div className="relative mt-1">
                            <input
                                type="text"
                                value={searchQuery}
                                onChange={e => setSearchQuery(e.target.value)}
                                placeholder="Nome, NF, CPF/CNPJ, N° Boleto..."
                                className="w-full p-2 pl-10 border rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
                            />
                            <Search size={18} className="absolute left-3 top-2.5 text-gray-400" />
                        </div>
                    </div>
                    <div className="md:col-span-3">
                        <label className="block text-sm font-medium text-gray-700 flex justify-between">
                            <span>Vencimento</span>
                            <button 
                                onClick={() => setFilterDateType(prev => prev === 'month' ? 'date' : 'month')}
                                className="text-xs text-blue-600 hover:underline flex items-center gap-1"
                            >
                                <RefreshCw size={10}/> {filterDateType === 'month' ? 'Mudar p/ Dia' : 'Mudar p/ Mês'}
                            </button>
                        </label>
                        <div className="relative mt-1">
                            <input
                                type={filterDateType === 'month' ? 'month' : 'date'}
                                value={filterDateValue}
                                onChange={e => setFilterDateValue(e.target.value)}
                                className="w-full p-2 pl-10 border rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
                            />
                            <CalendarIcon size={18} className="absolute left-3 top-2.5 text-gray-400" />
                        </div>
                    </div>
                    <div className="md:col-span-3">
                        <label className="block text-sm font-medium text-gray-700">Meio de Pagamento</label>
                        <div className="relative mt-1">
                            <select value={filterMeioPagamento} onChange={e => setFilterMeioPagamento(e.target.value)} className="w-full p-2 pl-10 border rounded-md text-sm appearance-none bg-white focus:ring-blue-500 focus:border-blue-500">
                                <option value="">Todos</option>
                                <option value="pix">PIX</option>
                                <option value="boleto">Boleto</option>
                                <option value="dinheiro">Dinheiro</option>
                            </select>
                            <DollarSign size={18} className="absolute left-3 top-2.5 text-gray-400" />
                        </div>
                    </div>
                    <div className="md:col-span-3">
                        <label className="block text-sm font-medium text-gray-700">Status</label>
                        <div className="relative mt-1">
                            <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="w-full p-2 pl-10 border rounded-md text-sm appearance-none bg-white focus:ring-blue-500 focus:border-blue-500">
                                <option value="Todos">Todos</option>
                                <option value="a vencer">A Vencer</option>
                                <option value="vencida">Vencida</option>
                                <option value="paga">Paga</option>
                            </select>
                            <Filter size={18} className="absolute left-3 top-2.5 text-gray-400" />
                        </div>
                    </div>
                </div>
            </div>

            {loading && <div className="flex justify-center items-center p-8"><Loader2 className="animate-spin text-blue-600" /></div>}
            {error && <p className="text-red-600 p-4 bg-red-100 rounded-md">{error}</p>}

            {!loading && !error && (
                <div className="space-y-4">
                    {faturas.length === 0 ? (
                        <div className="p-8 text-center bg-white rounded-lg shadow border border-dashed border-gray-300">
                            <Info className="mx-auto h-12 w-12 text-gray-400 mb-2" />
                            <h3 className="text-lg font-medium text-gray-900">Nenhuma fatura encontrada</h3>
                        </div>
                    ) : (
                        faturas.map(fatura => (
                            <div key={fatura.id} className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-100 hover:border-blue-200 transition-colors">
                                <div className="flex flex-col md:flex-row justify-between md:items-center p-4 bg-gray-50 border-b cursor-pointer hover:bg-gray-100" onClick={() => toggleFaturaExpand(fatura.id)}>
                                    <div className="flex-1 mb-2 md:mb-0">
                                        <div className="flex items-center gap-2">
                                            {getMeioPagamentoIcon(fatura.parcelas[0]?.meio_pagamento)}
                                            <h2 className="text-lg font-semibold text-gray-800">{fatura.fornecedor_nome}</h2>
                                        </div>
                                        <p className="text-sm text-gray-500 mt-1">
                                            NF: <span className="font-medium text-gray-700">{fatura.nf_numero || 'N/A'}</span> | 
                                            Emissão: <span className="font-medium text-gray-700">{formatDateUTC(fatura.data_emissao)}</span>
                                        </p>
                                    </div>
                                    <div className="flex items-center gap-4">
                                        <div className="text-right">
                                            <span className="text-xs text-gray-500 block uppercase tracking-wider">Total</span>
                                            <p className="text-lg font-bold text-gray-800">{formatCurrency(fatura.valor_total, selectedCurrency)}</p>
                                        </div>
                                        <div className="flex items-center gap-1">
                                            {canEdit && <button onClick={(e) => { e.stopPropagation(); handleOpenEditModal(fatura.id); }} className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded" title="Editar"><Edit size={18} /></button>}
                                            {canDelete && <button onClick={(e) => { e.stopPropagation(); handleDeleteClick(fatura.id); }} className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded" title="Excluir"><Trash2 size={18} /></button>}
                                            <button onClick={(e) => { e.stopPropagation(); handleOpenPagamento(fatura); }} disabled={!fatura.parcelas.some(p => p.status === 'a vencer' || p.status === 'vencida')} className="ml-2 px-3 py-1.5 text-xs font-bold uppercase tracking-wider text-white bg-green-600 rounded hover:bg-green-700 disabled:bg-gray-300 shadow-sm">Pagar</button>
                                            <div className="ml-2 p-1 text-gray-400">{expandedFaturaId === fatura.id ? <ChevronUp size={20} /> : <ChevronDown size={20} />}</div>
                                        </div>
                                    </div>
                                </div>
                                {expandedFaturaId === fatura.id && (
                                    <div className="overflow-x-auto bg-gray-50/50 p-2 border-t border-gray-100">
                                        <table className="min-w-full divide-y divide-gray-200">
                                            <thead className="bg-gray-50">
                                                <tr>
                                                    <th className="px-4 py-2 text-xs font-bold text-left text-gray-500 uppercase">Status</th>
                                                    <th className="px-4 py-2 text-xs font-bold text-left text-gray-500 uppercase">Parcela</th>
                                                    <th className="px-4 py-2 text-xs font-bold text-left text-gray-500 uppercase">Vencimento</th>
                                                    <th className="px-4 py-2 text-xs font-bold text-left text-gray-500 uppercase">Valor</th>
                                                    <th className="px-4 py-2 text-xs font-bold text-left text-gray-500 uppercase">Pagamento</th>
                                                </tr>
                                            </thead>
                                            <tbody className="bg-white divide-y divide-gray-200">
                                                {fatura.parcelas.map(p => (
                                                    <tr key={p.id} className="hover:bg-gray-50">
                                                        <td className="px-4 py-3 whitespace-nowrap">{getStatusBadge(p.status)}</td>
                                                        <td className="px-4 py-3 whitespace-nowrap font-medium text-gray-900">{p.numero_parcela}</td>
                                                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">{formatDateUTC(p.data_vencimento)}</td>
                                                        <td className="px-4 py-3 whitespace-nowrap font-semibold text-gray-900">{formatCurrency(p.valor_parcela, selectedCurrency)}</td>
                                                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                                                            {p.status === 'paga' ? <span className="text-green-600 flex items-center gap-1"><CheckCircle size={14}/> {formatDateUTC(p.data_pagamento)}</span> : '-'}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        ))
                    )}
                    {totalPages > 1 && faturas.length > 0 && (
                        <div className="mt-6 flex items-center justify-between p-4 bg-white rounded-lg shadow-sm border border-gray-200">
                            <span className="text-sm text-gray-500">Página <span className="font-bold text-gray-800">{currentPage}</span> de <span className="font-bold text-gray-800">{totalPages}</span></span>
                            <div className="flex gap-2">
                                <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} className="px-3 py-1.5 border rounded-md hover:bg-gray-100 disabled:opacity-50 text-sm font-medium flex items-center gap-1"><ArrowLeft size={16} /> Anterior</button>
                                <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} className="px-3 py-1.5 border rounded-md hover:bg-gray-100 disabled:opacity-50 text-sm font-medium flex items-center gap-1">Próxima <ArrowRight size={16} /></button>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {isNovaFaturaOpen && (
                <NovaFaturaModal 
                    onClose={handleCloseModals}
                    onSave={handleSave}
                    formatCurrency={formatCurrency}
                />
            )}
            
            {editingFaturaId && (
                <EditFaturaModal
                    onClose={handleCloseModals}
                    onSave={handleSave}
                    faturaId={editingFaturaId}
                    formatCurrency={formatCurrency}
                />
            )}

            {deletingFaturaId && (
                <ConfirmDeleteModal
                    onClose={handleCloseModals}
                    onConfirm={handleConfirmDelete}
                    loading={deleteLoading}
                    error={deleteError} 
                />
            )}
            
            {isPagamentoOpen && selectedFatura && (
                <PagamentoModal
                    onClose={handleCloseModals}
                    onSave={handleSave}
                    fatura={selectedFatura}
                    accounts={accounts}
                    accountTypes={accountTypes}
                    formatCurrency={formatCurrency}
                    selectedCurrency={selectedCurrency}
                />
            )}
        </div>
    );
};

export default ContasAPagarPage;